import type { Dayjs } from 'dayjs'
import type { NativeSelectOption } from '@shared/ui/fields'
import type {
  DisciplineAllocationLog,
  DisciplineAllocationTutor,
  DisciplineScheduleAvailability,
  DisciplineScheduleDay,
  DisciplineScheduleGridDay,
} from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import {
  DisciplineAllocationStatus,
  DisciplineScheduleFlow,
  DisciplineScheduleStep,
} from './enums'

const dayjs = useDayjs()

/** The picker's columns, Sunday first as in legacy. */
const SCHEDULE_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export const SCHEDULE_DAY_NAMES: Record<string, string> = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
}

/** The availability window the legacy picker assumed until the API says otherwise. */
export const SCHEDULE_WEEKS_COUNT = 8

const SCHEDULE_LESSON_DURATION = 45

const SCHEDULE_MAX_PACE = 4

/** A lesson cannot be scheduled within 24 h of the customer's now. */
const SCHEDULE_START_LEAD_HOURS = 24

export const SCHEDULE_COMMENT_MAX_LENGTH = 512

export const SCHEDULE_CONFIRM_TEXT = 'I confirm that a tutor cannot be assigned less than 24h before the lesson. '
  + 'I confirm that <strong>I have agreed with the tutor to assign them less than 24 hours</strong>'

export const SCHEDULE_FLOW_STEPS: Record<DisciplineScheduleFlow, DisciplineScheduleStep[]> = {
  [DisciplineScheduleFlow.Create]: [
    DisciplineScheduleStep.Slots,
    DisciplineScheduleStep.StartDate,
    DisciplineScheduleStep.Pace,
    DisciplineScheduleStep.Filters,
    DisciplineScheduleStep.AssignTutor,
    DisciplineScheduleStep.Confirm,
  ],
  [DisciplineScheduleFlow.Change]: [
    DisciplineScheduleStep.Slots,
    DisciplineScheduleStep.EffectiveDate,
    DisciplineScheduleStep.Confirm,
  ],
  [DisciplineScheduleFlow.ChangeTutor]: [
    DisciplineScheduleStep.TutorReason,
    DisciplineScheduleStep.Slots,
    DisciplineScheduleStep.Pace,
    DisciplineScheduleStep.Filters,
    DisciplineScheduleStep.AssignTutor,
    DisciplineScheduleStep.Confirm,
  ],
  [DisciplineScheduleFlow.SetupLessons]: [
    DisciplineScheduleStep.SetupLessons,
  ],
}

export const getScheduleStepSibling = (
  flow: DisciplineScheduleFlow,
  step: DisciplineScheduleStep,
  offset: 1 | -1,
): DisciplineScheduleStep | null => {
  const steps = SCHEDULE_FLOW_STEPS[flow]

  return steps[steps.indexOf(step) + offset] ?? null
}

const toHour = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

/** Every hour of every day, inside the tutor's schedule and free: the Create picker has no tutor to check. */
export const createScheduleGrid = (): DisciplineScheduleGridDay[] => SCHEDULE_DAYS.map(day => ({
  day,
  slots: Array.from({ length: 24 }, (_, hour) => ({
    day,
    start_time: toHour(hour),
    duration: SCHEDULE_LESSON_DURATION,
    week_cycle: 1,
    in_schedule: true,
    busy_slots: [],
    disabled: false,
  })),
}))

/** The current tutor's hours: an hour busy in every week of the window cannot be picked. */
export const buildAvailabilityGrid = (data: DisciplineScheduleAvailability): DisciplineScheduleGridDay[] => {
  const availability = Array.isArray(data.availability) ? {} : data.availability

  return createScheduleGrid().map(({ day, slots }) => ({
    day,
    slots: slots.map((slot) => {
      const hour = availability[day]?.[slot.start_time.slice(0, 2)]
      const busySlots = hour?.busy_slots ?? []

      return {
        ...slot,
        in_schedule: hour?.in_schedule === true,
        busy_slots: busySlots,
        disabled: busySlots.length >= data.weeks_ahead,
      }
    }),
  }))
}

const isSameScheduleSlot = (a: DisciplineScheduleDay, b: DisciplineScheduleDay): boolean =>
  a.day === b.day && a.start_time === b.start_time

export const toScheduleDay = (slot: DisciplineScheduleDay): DisciplineScheduleDay => ({
  day: slot.day,
  start_time: slot.start_time,
  duration: slot.duration,
  week_cycle: slot.week_cycle,
})

export const isScheduleSlotSelected = (selected: DisciplineScheduleDay[], slot: DisciplineScheduleDay): boolean =>
  selected.some(item => isSameScheduleSlot(item, slot))

export const toggleScheduleSlot = (
  selected: DisciplineScheduleDay[],
  slot: DisciplineScheduleDay,
): DisciplineScheduleDay[] => (isScheduleSlotSelected(selected, slot)
  ? selected.filter(item => !isSameScheduleSlot(item, slot))
  : [...selected, toScheduleDay(slot)])

export const isScheduleDaySelected = (selected: DisciplineScheduleDay[], slots: DisciplineScheduleDay[]): boolean =>
  slots.length > 0 && slots.every(slot => isScheduleSlotSelected(selected, slot))

/** A day header selects every free hour of the day, or clears them all when they are all selected already. */
export const toggleScheduleDay = (
  selected: DisciplineScheduleDay[],
  slots: DisciplineScheduleDay[],
): DisciplineScheduleDay[] => {
  if (!slots.length) return selected

  if (isScheduleDaySelected(selected, slots)) {
    return selected.filter(item => !isScheduleSlotSelected(slots, item))
  }

  return [...selected, ...slots.filter(slot => !isScheduleSlotSelected(selected, slot)).map(toScheduleDay)]
}

export const formatScheduleBusyNotes = (busySlots: string[], weeksCount: number): string => [
  `Partly-busy: ${weeksCount - busySlots.length}/${weeksCount}`,
  `${busySlots.length} busy slots:`,
  ...busySlots.map(slot => dayjs(slot).format(DateFormat.KanbanDate)),
].join('\n')

const dayOrder = (day: string): number => SCHEDULE_DAYS.indexOf(day as typeof SCHEDULE_DAYS[number])

/** The API does not keep the days in weekday order. */
export const sortScheduleDays = <T extends DisciplineScheduleDay>(days: T[]): T[] =>
  [...days].sort((a, b) => dayOrder(a.day) - dayOrder(b.day) || a.start_time.localeCompare(b.start_time))

export const formatScheduleDay = (slot: DisciplineScheduleDay): string => `${slot.day}, ${slot.start_time}`

export const formatSchedulePace = (pace: number, code: string): string =>
  `${pace} ${code.toLowerCase()} ${pace === 1 ? 'lesson' : 'lessons'}/week`

/** 0 is listed but cannot be picked: it is what an unset pace shows. */
export const getSchedulePaceOptions = (selected: number): NativeSelectOption[] =>
  Array.from({ length: SCHEDULE_MAX_PACE + 1 }, (_, value) => ({
    id: value,
    value,
    text: `${value} lesson${value === 1 ? '' : 's'} per week`,
    selected: value === selected,
    disabled: value === 0,
  }))

/** Every date the schedule endpoints take is a customer calendar date. */
export const getCustomerNow = (timezone: string | null | undefined): Dayjs =>
  (timezone ? dayjs().tz(timezone) : dayjs())

export const getScheduleStartMinDate = (customerNow: Dayjs): string =>
  customerNow.add(SCHEDULE_START_LEAD_HOURS, 'hour').format(DateFormat.DefaultDate)

/** True while a changed schedule waits for its start date. Both are 'Y-m-d H:i:s' in the admin's view timezone. */
export const isSchedulePending = (effectiveDate: string | null | undefined, now: string): boolean =>
  !!effectiveDate && effectiveDate > now

export const getAllocationStatus = (availability: number): DisciplineAllocationStatus => {
  if (availability === 100) return DisciplineAllocationStatus.Available
  if (availability >= 50) return DisciplineAllocationStatus.PartlyAvailable

  return DisciplineAllocationStatus.Busy
}

export const getAllocationLabel = (log: DisciplineAllocationLog): string => {
  switch (getAllocationStatus(log.availability)) {
    case DisciplineAllocationStatus.Available:
      return 'Available: all'
    case DisciplineAllocationStatus.PartlyAvailable:
      return `Partly busy: ${log.busy_count}/${log.total_count}`
    default:
      return `Busy: ${log.busy_count}/${log.total_count}`
  }
}

export const getAllocationTooltip = (log: DisciplineAllocationLog): string =>
  getAllocationStatus(log.availability) === DisciplineAllocationStatus.Available
    ? 'Available: all slots'
    : `${log.busy_count} busy slots out of ${log.total_count}`

export const getAllocationTutorId = (log: DisciplineAllocationLog): number | null => log.slot_pair[0]?.tutor_id ?? null

/** A log whose tutor is no longer assignable has no tutor row and is not offered. */
export const findAllocationTutor = (
  log: DisciplineAllocationLog,
  tutors: DisciplineAllocationTutor[],
): DisciplineAllocationTutor | null => {
  const tutorId = getAllocationTutorId(log)

  return tutorId ? tutors.find(tutor => tutor.id === tutorId) ?? null : null
}

export const getAllocationTutorName = (tutor: DisciplineAllocationTutor | null): string =>
  tutor ? `${tutor.first_name || '-'} ${tutor.last_name ?? ''}`.trim() : '-'
